// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ButtonIconPresentationProps, ButtonPresentationProps } from "@insite/mobius/Button";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import Filter from "@insite/mobius/Icons/Filter";
import { getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { color } from "@rugby/XDL/Utilities";
import { css } from "styled-components";

interface FilterHeaderStyle {
    container?: any;
    countGridItem?: any;
    countNumber?: InjectableCss;
    toggleFilterGridItem?: any;
    headingText?: InjectableCss;
    headingItem?: any;
    orderCountText?: InjectableCss;
    clearFiltersButton?: any;
    appliedFiltersContainer?: InjectableCss;
    toggleFilterButtonIcon?: ButtonIconPresentationProps;
    toggleFilterButton?: ButtonPresentationProps;
}

export const filterHeaderStyle: FilterHeaderStyle = {
    // Header Section
    container: {
        gap: 0,
    },

    countGridItem: {
        width: 7,
        css: css`
            order: 1;
            align-items: center;
            justify-content: flex-end;
            font-size: 16px;
            font-weight: 400;
        `,
    },

    countNumber: {
        css: css`
            display: inline-block;
            margin-right: 3px;
            font-weight: bold;
        `,
    },

    toggleFilterGridItem: {
        width: 5,
        css: css`
            justify-content: flex-start;
        `,
    },

    headingText: {
        css: css`
            padding-left: 5px;
            margin-bottom: 0;
            font-weight: 400;
            font-size: 18px;
            display: block;
        `,
    },

    headingItem: {
        css: css`
            display: none;
        `,
    },

    orderCountText: {
        css: css`
            justify-content: flex-end;
            display: flex;
        `,
    },
    toggleFilterButtonIcon: {
        mergeCss: true,
        src: Filter,
        size: 20,
        color: "text.main",
        css: css`
            &&& {
                width: 65px;
                position: relative;
                font-weight: 400;
                font-size: 18px;
                color: ${color("text")};
            }
            &::after {
                content: "Filter";
                position: absolute;
                top: 50%;
                right: 0px;
                transform: translateY(-50%);
                font-size: 18px;
                font-weight: 400;
                color: ${color("text")};
            }
        `,
    },
    toggleFilterButton: {
        mergeCss: true,
        css: css`
            display: flex;
            align-items: center;
            justify-content: center;
            border: 1px solid ${color("common.border")};
            border-radius: 0;
            background: ${color("common.ui3")};
            padding: 8px 15px;
            min-width: 140px;
            &:disabled {
                background: ${color("common.disabled")};
                border-color: ${color("common.disabled")}; // to remove border-color from OOTB
            }

            &:hover:not([disabled]) {
                background: ${color("common.ui3")};
                border: 1px solid ${color("common.border")};
            }

            &:focus:not([disabled]) {
                background: ${color("common.ui3")};
                border: 1px solid ${color("common.border")};
                outline-offset: 0px;
                outline: ${getProp("theme.focus.color", "#09f")} solid 2px;
                box-shadow: none;
            }
        `,
    },
    clearFiltersButton: {
        variant: "secondary",
        css: css`
            border-radius: 3px;
        `,
    },

    appliedFiltersContainer: {
        css: css`
            margin-top: 20px;
        `,
    },
};

interface FilteredTableStyle {
    container?: InjectableCss;
    lastCell?: InjectableCss;
    orderTotalCells?: InjectableCss;
    noResultsContainer?: InjectableCss;
}

export const filteredTableStyle: FilteredTableStyle = {
    container: {
        css: css`
            margin-top: 20px;
            overflow-x: auto;
        `,
    },

    lastCell: {
        css: css`
            justify-content: flex-end;
            text-align: right;

            button {
                justify-content: flex-end;
            }
        `,
    },

    orderTotalCells: {
        css: css`
            text-align: left;
        `,
    },

    noResultsContainer: {
        css: css`
            padding: 20px 0;
        `,
    },
};

interface FilteredSearchFieldsStyle {
    searchFieldsWrapper?: InjectableCss;
    container?: InjectableCss;
    fromDate?: any;
    toDate?: any;
    containerWithPadding?: InjectableCss;
}

export const filteredSearchFieldsStyle: FilteredSearchFieldsStyle = {
    // Added for pages the the search filter bbuild from cms
    container: {
        css: css`
            border: 1px solid ${getColor("common.border")};
            padding: 20px;
            margin-bottom: 20px;
            width: 100%;

            > div[class^="GridItemStyle"] {
                padding: 3px;
            }

            div[class^="GridItemStyle"] {
                align-items: flex-end;
            }
        `,
    },

    // wrapper for search filter that not combined from cms
    containerWithPadding: {
        css: css`
            border: 1px solid ${getColor("common.border")};
            padding: 25px 20px;
            margin-bottom: 20px;
            width: 100%;
            margin-top: -5px;
            display: flex;
            flex-wrap: wrap;

            > div[class^="GridOffset"] {
                width: auto;
                margin: -10px;

                > div[class^="GridItemStyle"] {
                    padding: 10px;
                }
            }

            > div[class^="Wrapper"] > div[class^="GridWrapper"] {
                > div[class^="GridOffset"] {
                    width: auto;
                    margin: -10px -10px 10px;
                }
            }

            div[class^="GridOffset"] {
                width: auto;
                flex-grow: 1;
            }
        `,
    },

    searchFieldsWrapper: {
        css: css`
            width: 100%;
            padding: 5px;
        `,
    },

    fromDate: {
        cssOverrides: {
            formField: css`
                padding-right: 10px;
            `,
        },
    },

    toDate: {
        cssOverrides: {
            formField: css`
                padding-left: 10px;
            `,
        },
    },
};

interface TableStyles {
    shipToCell?: DataTableCellBaseProps;
}

export const listTableStyles: TableStyles = {
    shipToCell: {
        css: css`
            max-width: 250px;
            white-space: pre-wrap;
        `,
    },
};
